<script lang="ts" setup>
import {
  EvaluationCriteriaConfiguration,
  EvaluationRelevantInstance,
} from "types/EvaluationResults";
import type { ComputedRef } from 'vue';

import { CriteriaInstance } from "~/types/Checklist";

const props = defineProps({
  criteriaInstance: {
    type: Object as PropType<CriteriaInstance>,
    required: true,
  },
  criteriaConfig: {
    type: Object as PropType<EvaluationCriteriaConfiguration>,
    required: true,
  },
  relevantInstance: {
    type: Object as PropType<EvaluationRelevantInstance>,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  hasMultipleInstances: {
    type: Boolean,
    required: true,
  },
});

const documentId = inject("documentId") as any;
const inheritedReviewInstance = inject<ComputedRef<EvaluationRelevantInstance | null> | null>('metricReviewRootInstance', null);
const rootReviewInstance = computed(() => inheritedReviewInstance?.value ?? props.relevantInstance);
const flagController = useNewDialogController();

const showParagarphPoints = computed(() => {
  return props.criteriaConfig.acceptParagraphPoints == true;
});

const requiresReview = computed(() => {
  return props.criteriaConfig.manualInstanceReview == true;
});

const documentActiveTypeStore = useDocumentActiveTypeStore(documentId);
const { hasActiveTypes, activeSubTypes, activeTypes, activeCriteria } =
  storeToRefs(documentActiveTypeStore);

const store = useResultsForCriteriaIdStore(documentId, props.criteriaInstance);
const {
  updateManualParagraphPoints,
  saveEvaluationResults,
  acceptEvaluation,
  updateManualPoints,
} = store;

const { relevantInstancesForParagraph } = storeToRefs(store);

const setManualReview = async (isValid: boolean) => {
  props.relevantInstance.isReviewed = true;
  props.relevantInstance.valid = isValid;
  acceptEvaluation();
  updateManualPoints(undefined);
  await saveEvaluationResults();
};

const appUserData = useAppUserDataStore();
const { isUserProductReviewer } = storeToRefs(appUserData);

const showAddToDictionary = computed(() => {
  return props.criteriaConfig.useWordsToIgnore == true;
});

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isTeacherViewMode } = storeToRefs(documentViewModeStore);

const chooseRelevantInstance = async () => {
  await useChooseRelevantInstance(
    documentId,
    relevantInstancesForParagraph.value(props.index),
    props.relevantInstance,
    activeTypes.value,
    activeSubTypes.value
  );
};
</script>

<template>
  <span
    :key="`instance-${requiresReview}`"
    class="shrink-0 h-[30px] pr-2 flex flex-row items-center"
  >
    <span
      v-if="
        isTeacherViewMode &&
        relevantInstance.valid &&
        !showParagarphPoints &&
        requiresReview != true &&
        criteriaConfig.hidePassLabel != true
      "
      class="text-success-default h-[30px] text-[20px] flex flex-row items-center"
    >
      <icon name="material-symbols:check-circle" />
    </span>
    <span
      v-if="isTeacherViewMode && requiresReview == true && !showParagarphPoints"
      class="flex flex-row items-center"
    >
      <EvaluationResultsInstancesListIgnoreWordChoice
        :criteria-instance="props.criteriaInstance"
        :text="relevantInstance.text"
        :disabled="showAddToDictionary != true"
      >
        <BaseTextButton
          color="success"
          class="h-[30px] !opacity-100"
          :class="{
            'bg-success bg-opacity-10':
              relevantInstance.valid != true &&
              relevantInstance.isReviewed == true,
          }"
          @click="setManualReview(false)"
        >
          correct
        </BaseTextButton>
      </EvaluationResultsInstancesListIgnoreWordChoice>

      <span class="mx-1">|</span>
      <BaseTextButton
        color="danger"
        class="h-[30px]"
        :class="{
          'bg-danger-default bg-opacity-10':
            relevantInstance.valid == true &&
            relevantInstance.isReviewed == true,
        }"
        @click="setManualReview(true)"
      >
        incorrect
      </BaseTextButton>
    </span>
    <span
      v-if="
        isTeacherViewMode &&
        relevantInstance.valid != true &&
        !showParagarphPoints &&
        requiresReview != true &&
        criteriaConfig.hidePassLabel != true
      "
      class="text text-warning-default text-body-1 shrink-0 leading-[30px] -mt-[1px]"
    >
      please review
    </span>

    <span v-if="isUserProductReviewer == true">
      <BasePopover align="end" :controller="flagController">
        <div
          class="h-[30px] w-[30px] ml-1 active:bg-opacity-20 flex flex-col items-center p-2 justify-center rounded hover:bg-medium-default hover:bg-opacity-10 transition-all duration-150 cursor-pointer"
        >
          <icon name="material-symbols:flag" />
          <span class="sr-only">Flag {{ relevantInstance.text || 'sentence' }} suggestion</span>
        </div>
        <template #content>
          <EvaluationResultsFlagReviewForm
            :criteria-instance="criteriaInstance"
            target-kind="instance"
            :target-uid="relevantInstance.uid"
            :target-label="relevantInstance.text || 'Evaluated sentence'"
            :root-instance="rootReviewInstance"
            @saved="flagController.close()"
          />
        </template>
      </BasePopover>
    </span>

    <BaseTextButton
      v-if="
        hasMultipleInstances &&
        isTeacherViewMode != true &&
        criteriaConfig.manualInstanceReview != true
      "
      @click="chooseRelevantInstance"
    >
      choose
    </BaseTextButton>
  </span>
</template>

<style scoped></style>
